/**
 * @file useSectionReveal.ts
 * @description Показать замечания после неудачного «Сохранить блок»
 * (phase19.16, SB122; ревью партии SB122): номер неудачной попытки
 * становится запросом `reveal` для видимой панели секции — она раскрывает
 * первую группу с ошибкой и даёт фокус полю (`RevealIssueGroup`). Если
 * замечания только на другой вкладке секции — сначала переключаемся на неё;
 * в панели элемента вкладку не меняем.
 *
 * SB120 этап 1 (§7.19 фазы: «Есть замечания (N) — а где исправлять?»):
 * `jump(path)` — переход из списка замечаний к полю: вкладка этого пути,
 * `revealPath` — раскрыть именно его группу и дать фокус полю. Выбор
 * элемента (панель элемента) делает вызывающий до `jump`.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { CONTENT_ISSUE_RULES, groupsWithIssues, LOOK_ISSUE_RULES } from "../../lib/site-builder/issue-groups";

export type SectionTab = "content" | "look";

/** Правила групп вкладки. */
const TAB_RULES = { content: CONTENT_ISSUE_RULES, look: LOOK_ISSUE_RULES } as const;

/** Вкладка, где показывать замечания: текущая, если в ней они есть, иначе та, где есть. */
export function revealTab(paths: readonly string[], shown: SectionTab): SectionTab {
  const other: SectionTab = shown === "content" ? "look" : "content";
  const here = groupsWithIssues(paths, TAB_RULES[shown]).size > 0;
  const there = groupsWithIssues(paths, TAB_RULES[other]).size > 0;
  return !here && there ? other : shown;
}

interface Deps {
  /** Счётчик неудачных сохранений из-за замечаний (`useSectionDraft`). */
  failedSaves: number;
  issues: ReadonlyArray<{ path: string }>;
  tab: SectionTab;
  setTab: (tab: SectionTab) => void;
  /** Открыта панель элемента — вкладку не трогаем. */
  inElement: boolean;
}

export interface SectionReveal {
  reveal: number | null;
  revealDone: () => void;
  /** Путь поля из списка замечаний; `null` — первая группа с ошибкой. */
  revealPath: string | null;
  /** Перейти к полю: `element` — путь в панели элемента (вкладку не трогаем). */
  jump: (path: string, element: boolean) => void;
}

export function useSectionReveal({ failedSaves, issues, tab, setTab, inElement }: Deps): SectionReveal {
  const [reveal, setReveal] = useState<number | null>(null);
  const [revealPath, setRevealPath] = useState<string | null>(null);
  // Ключи переходов — отрицательные, чтобы не совпасть с номером неудачного сохранения.
  const jumps = useRef(0);
  const revealDone = useCallback(() => {
    setReveal(null);
    setRevealPath(null);
  }, []);
  const latest = useRef({ issues, tab, setTab, inElement });
  latest.current = { issues, tab, setTab, inElement };
  useEffect(() => {
    if (failedSaves === 0) return;
    const now = latest.current;
    if (!now.inElement) {
      const target = revealTab(
        now.issues.map((i) => i.path),
        now.tab,
      );
      if (target !== now.tab) now.setTab(target);
    }
    setRevealPath(null);
    setReveal(failedSaves);
  }, [failedSaves]);
  const jump = useCallback((path: string, element: boolean) => {
    const now = latest.current;
    if (!element) {
      const target = revealTab([path], now.tab);
      if (target !== now.tab) now.setTab(target);
    }
    jumps.current += 1;
    setRevealPath(path);
    setReveal(-jumps.current);
  }, []);
  return { reveal, revealDone, revealPath, jump };
}
